var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import labelStyles from '@cof/fs-field-label-core/lib/field-label.lit.min.js';
import { FormControlMixin } from '@cof/fs-form-control';
import formFieldStyles from '@cof/fs-form-field-core/lib/form-field.lit.min.js';
import radioStyles from '@cof/fs-radio-core/lib/radio.lit.min.js';
import { css, html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
const eventForwarder = (target) => (event) => {
    event.stopPropagation();
    const e = new Event(event.type, event);
    target.dispatchEvent(e);
};
/**
 * TODO: Eventually transform the event name into a Symbol
 * whenever browsers get that behavior implemented
 *
 * This is a private event name that will be dispatched on the
 * radio's host. Each radio will include attach its own listener
 * to its host to determine which radio should remain checked
 * at any given moment.
 */
const privateChangeEvent = 'fsds-private-radio-change';
class PrivateChangeEvent extends Event {
    constructor(checkedRadio) {
        super(privateChangeEvent, {
            bubbles: false,
            cancelable: false,
            composed: false
        });
        this.checkedRadio = checkedRadio;
    }
}
/**
 * The web component implementation of the Financial Services radio component
 *
 * ## Installation
 * ### Install the node module:
 *
 * ```bash
 * npm i @cof/fs-radio-wc
 * ```
 *
 * ## Usage
 *
 * To import the side-effectful registration of `<fs-radio>` call
 *
 * ```javascript
 * import '@cof/fs-radio-wc/fs-radio.js';
 * ```
 *
 * To import the `FsRadio` class as either a base class or to self-register the custom element use
 *
 * ```javascript
 * import { FsRadio } from '@cof/fs-radio-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 * ```html
 * <form>
 *  <fieldset class="fs-fieldset">
 *    <fs-radio name="radio" value="radio-one">Radio one</fs-radio>
 *    <fs-radio name="radio" value="radio-two">Radio two</fs-radio>
 *    <fs-radio name="radio" value="radio-three">Radio three</fs-radio>
 *  </fieldset>
 * </form>
 * ```
 *
 * @element fs-radio
 * @fires change - Notifies when the radio value has changed
 * @slot - The radio label text
 */
export class FsRadio extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /**
         * When true this element will be selected,
         * proxy of HTMLInputElement.prototype.checked.
         */
        this.checked = false;
        /** When true this radio element will be non-interactive. */
        this.disabled = false;
        /** When set to true the radio button will be smaller in size. */
        this.compact = false;
        /** Determines if the control is focusable */
        this.isFocusable = true;
        /** Forward DOM events as if they originated from this element */
        this.forwardEvent = eventForwarder(this);
        /**
         * Change events forwarded from this element bubble,
         * catch them at the root node and manage the group value
         */
        this._onHostChange = (event) => {
            const target = event.checkedRadio;
            if (target !== this && target.name === this.name && this.checked) {
                this.checked = false;
                this._internals.setFormValue(null);
            }
            this.isFocusable = this.checked;
        };
    }
    static get styles() {
        return [
            radioStyles,
            formFieldStyles,
            labelStyles,
            css `:host { display: block; } :host(:focus) { box-shadow: none !important; outline: 0; }`
        ];
    }
    /** Where possible, delegate focus to the internal input */
    static get shadowRootOptions() {
        return {
            mode: 'open',
            delegatesFocus: true
        };
    }
    render() {
        return html `
      <div
        class="fs-form-field fs-form-field--inline ${this.compact ? 'fs-form-field--compact' : ''}"
      >
        <input
          type="radio"
          name="${this.name}"
          ?disabled="${this.disabled}"
          class="fs-radio ${this.compact ? 'fs-radio--compact' : ''} ${this.showError ? 'fs-radio--error' : ''}"
          id="radio"
          .value="${this.value}"
          @change="${this._onChange}"
          .checked="${this.checked}"
          value="${this.value}"
          @keydown="${this._onKeydown}"
          @focus="${(e) => e.target.click()}"
          tabindex="${this.getTabIndex()}"
        >
        <label
          for="radio"
          class="
            fs-field-label
            fs-field-label--item
            ${this.compact ? 'fs-field-label--compact' : ''}
            ${this.disabled ? 'fs-field-label--disabled' : ''}
          "
        >
          <slot></slot>
        </label>
    </div>
    `;
    }
    /** Wire up the FormControl and set up the _onHostChange listener */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    firstUpdated(changed) {
        super.firstUpdated(changed);
        this.checked = this.hasAttribute('checked') || this.checked;
        this.linkControl(this.shadowRoot.querySelector('input'));
    }
    /**
     * Using connectedCallback here to account for instances where a radio
     * might be disconnected from the DOM then reconnected later. Often frameworks
     * or browsers will hold nodes in memory to avoid expensive node creation operations.
     * Doing this in connectedCallback rather than firstUpdated accounts for this use case.
     */
    connectedCallback() {
        super.connectedCallback();
        this.getRootNode().addEventListener(privateChangeEvent, this._onHostChange);
    }
    /** Tear down the _onHostChangeListener */
    disconnectedCallback() {
        super.disconnectedCallback();
        this.getRootNode().removeEventListener(privateChangeEvent, this._onHostChange);
    }
    /** Called when the form is reset, clears the value and resets checked */
    formResetCallback() {
        this._reset();
    }
    /** Manage form value on checked and value change */
    updated(changed) {
        if (changed.has('checked') || changed.has('value')) {
            this._internals.setFormValue(this.checked ? this.value : null);
        }
        if (changed.has('checked') && this.checked) {
            const privateChangeEvent = new PrivateChangeEvent(this);
            this.getRootNode().dispatchEvent(privateChangeEvent);
        }
    }
    /** Determines if the element can receive focus based on the type and current selection */
    getTabIndex() {
        if (this.checked) {
            return 0;
        }
        return this.isFocusable ? 0 : -1;
    }
    /** Keep checked state aligned with the input */
    _onChange(event) {
        /** Notify other radios they need to update */
        const privateChangeEvent = new PrivateChangeEvent(this);
        this.getRootNode().dispatchEvent(privateChangeEvent);
        /** Set current value on the element and emit a change event */
        this.checked = event.target.checked;
        this._internals.setFormValue(this.checked ? this.value : null);
        this.forwardEvent(event);
    }
    /** Handle focusing of elements on keydown events within the named group */
    _onKeydown(event) {
        /** Only worry about arrow keys */
        if (!['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'].includes(event.code)) {
            return;
        }
        const hostRoot = this.getRootNode();
        const options = hostRoot.querySelectorAll(`${this.localName}[name="${this.name}"]`);
        if (options.length > 1) {
            const index = Array.from(options).indexOf(this);
            const code = event.code;
            let next;
            if (code === 'ArrowDown' || code === 'ArrowRight') {
                next = options[index + 1] ? options[index + 1] : options[0];
            }
            else if (code === 'ArrowUp' || code === 'ArrowLeft') {
                next = options[index - 1] ? options[index - 1] : options[options.length - 1];
            }
            this._reset();
            next?.focus();
        }
    }
    /** Reset the internals instance for this named group and remove checked if present */
    _reset() {
        this.checked = this.hasAttribute('checked');
    }
}
/** The element's canonical name for use with safe define */
FsRadio.canonicalName = 'radio';
__decorate([
    property({ type: Boolean, reflect: false })
], FsRadio.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsRadio.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsRadio.prototype, "compact", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsRadio.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsRadio.prototype, "value", void 0);
__decorate([
    query('input')
], FsRadio.prototype, "input", void 0);
__decorate([
    state()
], FsRadio.prototype, "isFocusable", void 0);
//# sourceMappingURL=Radio.js.map